import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs'
import { Input } from '../../components/ui/Input'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import {
  Package,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Copy,
  TrendingUp,
  MapPin,
  DollarSign,
  Activity
} from 'lucide-react'
import { Package as PackageType } from '../../services/packages.service'
import { packagesService } from '../../services/packages.service'
import PackageForm from '../../components/admin/PackageForm'
import PackageList from '../../components/admin/PackageList'
import PackageStats from '../../components/admin/PackageStats'

interface PackageStats {
  totalPackages: number
  activePackages: number
  inactivePackages: number
  popularPackages: number
  packagesByType: Record<string, number>
  packagesByRegion: Record<string, number>
}

export default function PackageManagementPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [packages, setPackages] = useState<PackageType[]>([])
  const [regions, setRegions] = useState<string[]>([])
  const [stats, setStats] = useState<PackageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRegion, setSelectedRegion] = useState<string>('all')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingPackage, setEditingPackage] = useState<PackageType | null>(null)

  // Fetch data
  const fetchPackages = async () => {
    try {
      setLoading(true)
      const data = await packagesService.getAllPackages()
      setPackages(data)
    } catch (error) {
      console.error('Failed to fetch packages:', error)
      toast.error('Failed to load packages')
    } finally {
      setLoading(false)
    }
  }

  const fetchRegions = async () => {
    try {
      const data = await packagesService.getAllRegions()
      setRegions(data)
    } catch (error) {
      console.error('Failed to fetch regions:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const data = await packagesService.getPackageStatistics()
      setStats(data)
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  useEffect(() => {
    fetchPackages()
    fetchRegions()
    fetchStats()
  }, [])

  // Filter packages based on search and region
  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.type.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesRegion = selectedRegion === 'all' ||
                         pkg.serviceAreas.includes(selectedRegion) ||
                         pkg.serviceAreas.length === 0 // Nationwide packages

    return matchesSearch && matchesRegion
  })

  const handleCreatePackage = () => {
    setEditingPackage(null)
    setShowCreateForm(true)
  }

  const handleEditPackage = (pkg: PackageType) => {
    setEditingPackage(pkg)
    setShowCreateForm(true)
  }

  const handlePackageSaved = () => {
    setShowCreateForm(false)
    setEditingPackage(null)
    fetchPackages()
    fetchStats()
    toast.success('Package saved successfully')
  }

  const handleDeletePackage = async (pkg: PackageType) => {
    if (!confirm(`Are you sure you want to delete "${pkg.name}"?`)) {
      return
    }

    try {
      await packagesService.deletePackage(pkg.id)
      fetchPackages()
      fetchStats()
      toast.success('Package deleted successfully')
    } catch (error) {
      console.error('Failed to delete package:', error)
      toast.error('Failed to delete package')
    }
  }

  const handleClonePackage = async (pkg: PackageType) => {
    const targetRegion = prompt('Enter target region name:')
    if (!targetRegion) return

    try {
      await packagesService.clonePackageToRegion(pkg.id, targetRegion)
      fetchPackages()
      fetchStats()
      toast.success(`Package cloned to ${targetRegion}`)
    } catch (error) {
      console.error('Failed to clone package:', error)
      toast.error('Failed to clone package')
    }
  }

  if (showCreateForm) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => setShowCreateForm(false)}
            >
              ← Back to Packages
            </Button>
          </div>
          <PackageForm
            package={editingPackage}
            regions={regions}
            onSave={handlePackageSaved}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Package className="h-8 w-8 text-primary-600" />
              <span className="text-xl font-bold text-neutral-900">Package Management</span>
            </div>
            <div className="flex items-center space-x-4">
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                Admin Access
              </Badge>
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-semibold text-sm">AD</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">
              Package Management
            </h1>
            <p className="text-neutral-600">
              Manage funeral packages, pricing, and regional availability.
            </p>
          </div>
          <Button
            onClick={handleCreatePackage}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create Package</span>
          </Button>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview" className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="packages" className="flex items-center space-x-2">
              <Package className="h-4 w-4" />
              <span>Packages ({filteredPackages.length})</span>
            </TabsTrigger>
            <TabsTrigger value="regions" className="flex items-center space-x-2">
              <MapPin className="h-4 w-4" />
              <span>Regions ({regions.length})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {stats ? (
              <PackageStats stats={stats} />
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <LoadingSpinner />
                  <p className="mt-2 text-neutral-600">Loading statistics...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="packages" className="space-y-6">
            {/* Search and Filters */}
            <Card>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-neutral-400" />
                    <Input
                      placeholder="Search packages..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-neutral-400" />
                    <select
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      className="px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">All Regions</option>
                      {regions.map(region => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Package List */}
            {loading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <LoadingSpinner />
                  <p className="mt-2 text-neutral-600">Loading packages...</p>
                </CardContent>
              </Card>
            ) : (
              <PackageList
                packages={filteredPackages}
                onEdit={handleEditPackage}
                onDelete={handleDeletePackage}
                onClone={handleClonePackage}
              />
            )}
          </TabsContent>

          <TabsContent value="regions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5" />
                  <span>Available Regions</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {regions.map(region => {
                    const packageCount = packages.filter(pkg =>
                      pkg.serviceAreas.includes(region) || pkg.serviceAreas.length === 0
                    ).length

                    return (
                      <div key={region} className="p-4 border border-neutral-200 rounded-lg">
                        <div className="font-medium text-neutral-900">{region}</div>
                        <div className="text-sm text-neutral-600">{packageCount} packages</div>
                      </div>
                    )
                  })}
                  {regions.length === 0 && (
                    <div className="col-span-full text-center py-8 text-neutral-600">
                      No regions found. Create packages with service areas to see regions here.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}